import { Timesheetapi } from "../../middleware/interceptor";
import { normalizeEntries, toISO } from "./dashboardStats";

export const ENDPOINTS = {
  // ── NEW — fill these in (leave "" until the API exists) ──
  myTimesheetEntries: "api/Dashboard/GetMyTimesheetEntries",       // DashboardController.cs
  pendingApprovalsCount: "api/Dashboard/GetPendingApprovalsCount", // DashboardController.cs
  holidays: "",              // no holiday table yet — leave "" (GET ?year=YYYY if you add one)

  // ── EXISTING — already in use ──
  clients: "api/Projects/getclientname",
  projects: "api/Projects/getprojectdetails",
  employees: "api/Projects/GetAllEmployee",
  permissions: "api/Roles/GetPermissionsByRoleId",
};

export class NotConfiguredError extends Error {
  constructor(name) {
    super(`Endpoint "${name}" is not configured in dashboardApi.js`);
    this.name = "NotConfiguredError";
  }
}

// ── 1. My timesheet entries (required for all personal sections) ─────
export async function fetchMyEntries(empId) {
  if (!ENDPOINTS.myTimesheetEntries) throw new NotConfiguredError("myTimesheetEntries");
  const now = new Date();
  const res = await Timesheetapi.get(ENDPOINTS.myTimesheetEntries, {
    params: { empid: empId, fromDate: `${now.getFullYear()}-01-01`, toDate: toISO(now) },
  });
  // RESPONSE SHAPE: { status, entries: [{ entry_date, hours, is_billable, project_name, ... }], off_dates: [] }
  if (res?.data?.status === 0) throw new Error(res.data.message || "Timesheet entries API returned status 0");
  return {
    entries: normalizeEntries(res?.data?.entries ?? []),
    // Days covered by default rows (leave etc.) — not counted as missed
    offDates: Array.isArray(res?.data?.off_dates) ? res.data.off_dates : [],
  };
}

// ── 2. Company holidays (optional — otherwise only weekends are excluded) ──
export async function fetchHolidays(year) {
  if (!ENDPOINTS.holidays) return [];
  try {
    const res = await Timesheetapi.get(ENDPOINTS.holidays, { params: { year } });
    // RESPONSE SHAPE: [{ holiday_date: "2026-10-02" }, ...] or ["2026-10-02", ...]
    const rows = res?.data?.holidays ?? res?.data ?? [];
    return (Array.isArray(rows) ? rows : [])
      .map((h) => String(typeof h === "string" ? h : h.holiday_date ?? h.Holiday_date ?? h.date ?? "").slice(0, 10))
      .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
  } catch (err) {
    console.error("Holidays load failed (treating as none):", err);
    return [];
  }
}

// ── 3. Pending approvals count (optional — Sidebar badge) ───────────
export async function fetchPendingApprovalsCount(empId) {
  if (!ENDPOINTS.pendingApprovalsCount) return 0;
  try {
    const res = await Timesheetapi.get(ENDPOINTS.pendingApprovalsCount, { params: { empid: empId } });
    // RESPONSE SHAPE: { count: 4 } or a plain number
    const n = Number(res?.data?.count ?? res?.data?.Count ?? res?.data ?? 0);
    return Number.isFinite(n) ? n : 0;
  } catch (err) {
    console.error("Pending approvals count failed:", err);
    return 0;
  }
}

// ── 4. Sidebar permissions (existing) ───────────────────────────────
export async function fetchPermissions(roleId) {
  const res = await Timesheetapi.get(ENDPOINTS.permissions, { params: { roleId } });
  const map = {};
  (res?.data?.permissions || []).forEach((p) => {
    map[p.module_key] = !!p.has_access;
  });
  return map; // { dashboard: true, activity_card: true, ... }
}

// ── 5. Organization overview (existing APIs) ────────────────────────
export async function fetchOrgCounts(empId) {
  // allSettled: one failing API shouldn't blank the whole overview
  const [clientRes, projectRes, empRes] = await Promise.allSettled([
    Timesheetapi.get(ENDPOINTS.clients),
    Timesheetapi.get(ENDPOINTS.projects, { params: { Role_id: 3, empid: empId } }),
    Timesheetapi.post(ENDPOINTS.employees),
  ]);

  const out = {};

  if (clientRes.status === "fulfilled") {
    const clients = clientRes.value?.data?.Client || [];
    out.clients = new Set(clients.map((c) => c.Client_id ?? c.client_id)).size;
  } else console.error("Clients load failed:", clientRes.reason);

  if (projectRes.status === "fulfilled") {
    // De-dupe by project id first so active + inactive always adds up to total
    const byId = new Map();
    (projectRes.value?.data?.projects || []).forEach((p) => byId.set(p.Project_id ?? p.projectId, p));
    const projects = [...byId.values()];
    const isType = (p, t) => String(p.Project_type) === String(t);
    out.projects = projects.length;
    out.activeProjects = projects.filter((p) => String(p.status ?? p.Status) === "1").length;
    out.inactiveProjects = out.projects - out.activeProjects;
    out.opsProjects = projects.filter((p) => isType(p, 1)).length;
    out.onetimeProjects = projects.filter((p) => isType(p, 2)).length;
    out.annualProjects = projects.filter((p) => isType(p, 3)).length;
  } else console.error("Projects load failed:", projectRes.reason);

  if (empRes.status === "fulfilled") {
    const employees = empRes.value?.data?.Empdetails || [];
    out.activeEmployees = employees.filter((e) => {
      if (String(e.del_employee_status ?? "") === "1") return false;
      return e.employee_status == null ? true : String(e.employee_status) === "1";
    }).length;
  } else console.error("Employees load failed:", empRes.reason);

  return out;
}